<template>
  <div class="not-clocked-in-panel">
    <div
      class="stat-card stat-card-red stat-card-clickable"
      :class="{ 'stat-card-active': expanded }"
      @click="expanded = !expanded"
    >
      <div class="stat-icon"><b-icon-person-x-fill /></div>
      <div class="stat-body">
        <span class="stat-value">{{ notClockedIn.length }}</span>
        <span class="stat-label">Not Clocked In (Today)</span>
        <span class="stat-hint">{{ clockedIn.length }} clocked in today</span>
      </div>
    </div>

    <div v-if="expanded" class="attendance-card">
      <div class="attendance-card-header">
        <h3>Today's Attendance</h3>
        <span class="attendance-count">{{ tradies.length }} tradies</span>
      </div>

      <div v-if="loading" class="attendance-empty"><p>Loading…</p></div>

      <div v-else-if="tradies.length === 0" class="attendance-empty">
        <b-icon-clipboard class="empty-icon" />
        <p>No tradies found for this project.</p>
      </div>

      <template v-else>
        <div class="attendance-group">
          <h4 class="attendance-group-title">
            Clocked In Today ({{ clockedIn.length }})
          </h4>
          <div v-if="clockedIn.length === 0" class="attendance-group-empty">
            No one has clocked in yet.
          </div>
          <b-list-group v-else flush>
            <b-list-group-item
              v-for="tradie in clockedIn"
              :key="'in-' + tradie.id"
              class="tradie-row"
            >
              <b-avatar
                size="2rem"
                variant="primary"
                :src="tradieAvatarUrl(tradie)"
                :text="initials(tradie.full_name)"
                class="tradie-avatar"
              />
              <div class="tradie-copy">
                <span class="tradie-name">{{ tradie.full_name }}</span>
                <b-badge
                  v-if="tradie.clocked_in_source === 'manual'"
                  variant="secondary"
                  pill
                  class="ml-1"
                  style="font-size: 0.65rem"
                  title="Hours were entered manually, not from a device clock-in"
                  >Manually entered</b-badge
                >
                <b-badge
                  v-if="tradie.trade"
                  variant="light"
                  class="ml-1 tradie-trade"
                  >{{ formatTrade(tradie.trade) }}</b-badge
                >
              </div>
            </b-list-group-item>
          </b-list-group>
        </div>

        <div class="attendance-group">
          <h4 class="attendance-group-title">
            Not Clocked In ({{ notClockedIn.length }})
          </h4>
          <div v-if="notClockedIn.length === 0" class="attendance-group-empty">
            Everyone has clocked in today.
          </div>
          <b-list-group v-else flush>
            <b-list-group-item
              v-for="tradie in notClockedIn"
              :key="'out-' + tradie.id"
              class="tradie-row"
            >
              <b-avatar
                size="2rem"
                variant="secondary"
                :src="tradieAvatarUrl(tradie)"
                :text="initials(tradie.full_name)"
                class="tradie-avatar"
              />
              <div class="tradie-copy">
                <span class="tradie-name">{{ tradie.full_name }}</span>
                <b-badge
                  v-if="isInvitee(tradie)"
                  variant="warning"
                  pill
                  class="ml-1"
                  style="font-size: 0.65rem"
                  >Invited</b-badge
                >
                <b-badge
                  v-if="tradie.trade"
                  variant="light"
                  class="ml-1 tradie-trade"
                  >{{ formatTrade(tradie.trade) }}</b-badge
                >
              </div>
            </b-list-group-item>
          </b-list-group>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { avatarUrl } from '@/utils/avatar'
import { formatTrade } from '@/utils/timesheetHelpers'

export default {
  name: 'NotClockedInPanel',
  props: {
    projectId: { type: [Number, String], default: null },
  },
  data() {
    return {
      expanded: false,
      loading: false,
      tradies: [],
    }
  },
  computed: {
    isProjectScoped() {
      return this.$store.getters['auth/isProjectScoped']
    },
    supervisorProjectIds() {
      return this.$store.getters['auth/supervisorProjectIds'] || []
    },
    allProjects() {
      return this.$store.state.project.projects || []
    },
    // Same scoping rule the dashboard's project select already uses
    // (timesheet/projectOptions): a scoped supervisor only ever sees their
    // assigned projects, everyone else sees every loaded project. A single
    // projectId prop narrows this down to just that one project.
    scopedProjectIds() {
      if (this.projectId) return [this.projectId]
      if (this.isProjectScoped) return this.supervisorProjectIds
      return this.allProjects.map(function (p) {
        return p.id
      })
    },
    clockedIn() {
      return this.tradies.filter(function (t) {
        return t.clocked_in_today
      })
    },
    notClockedIn() {
      return this.tradies.filter(function (t) {
        return !t.clocked_in_today
      })
    },
  },
  watch: {
    scopedProjectIds() {
      this.refresh()
    },
  },
  created() {
    this.refresh()
  },
  methods: {
    formatTrade,
    tradieAvatarUrl(tradie) {
      return avatarUrl(tradie.avatar)
    },
    initials(str) {
      if (!str) return '?'
      return str
        .match(/(^\S\S?|\b\S)?/g)
        .join('')
        .match(/(^\S|\S$)?/g)
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },
    isInvitee(tradie) {
      return !!tradie.pending && !!tradie.has_token
    },
    async refresh() {
      var ids = this.scopedProjectIds
      if (ids.length === 0) {
        this.tradies = []
        return
      }
      this.loading = true
      try {
        // Single batched call for every project instead of one attendance
        // request per project — see timesheet/getAttendance.
        var res = await this.$store.dispatch('timesheet/getAttendance', ids)
        var byId = {}
        ;(res.data || []).forEach(function (project) {
          ;(project.workers || []).forEach(function (t) {
            var existing = byId[t.id]
            if (existing) {
              existing.clocked_in_today =
                existing.clocked_in_today || t.clocked_in_today
              // 'app' (a real device clock-in) wins over 'manual' if the
              // tradie shows up on more than one project today - being
              // genuinely on site anywhere outranks a manual entry elsewhere.
              if (existing.clocked_in_source !== 'app') {
                existing.clocked_in_source =
                  t.clocked_in_source || existing.clocked_in_source
              }
            } else {
              byId[t.id] = t
            }
          })
        })
        this.tradies = Object.values(byId)
      } catch (e) {
        this.$bvToast.toast('Failed to load attendance data.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.not-clocked-in-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stat-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  border-left-width: 4px;
}

.stat-card-clickable {
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.stat-card-active {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12) !important;
}

.stat-card-red {
  border-left-color: #ef4444;
  .stat-icon {
    color: #b91c1c;
    background: #fee2e2;
  }
  &.stat-card-active {
    border-color: #ef4444;
  }
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  font-size: 18px;
  flex-shrink: 0;
}

.stat-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  line-height: 1.1;
}

.stat-label {
  margin-top: 2px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.stat-hint {
  margin-top: 2px;
  font-size: 11px;
  color: #9ca3af;
}

.attendance-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.attendance-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    flex: 1;
  }
}

.attendance-count {
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 3px 10px;
  border-radius: 20px;
}

.attendance-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  color: #9ca3af;

  p {
    margin: 12px 0 0;
    font-size: 13px;
  }
}

.empty-icon {
  font-size: 2rem;
  color: #d1d5db;
}

.attendance-group {
  padding: 14px 16px;

  & + .attendance-group {
    border-top: 1px solid #f3f4f6;
  }
}

.attendance-group-title {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}

.attendance-group-empty {
  font-size: 13px;
  color: #9ca3af;
  padding: 8px 0;
}

.tradie-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}

.tradie-avatar {
  flex-shrink: 0;
}

.tradie-copy {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
}

.tradie-name {
  font-weight: 500;
  color: #111827;
}

.tradie-trade {
  color: #374151;
}
</style>
